import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRStore } from '@/stores';

interface UserPresence {
  id: string;
  name: string;
  avatar?: string;
  color: string;
  cursor: {
    line: number;
    column: number;
    visible: boolean;
  };
  lastActivity: number;
  isTyping?: boolean;
}

interface CollaborationState {
  roomId: string | null;
  users: UserPresence[];
  currentUser: UserPresence | null;
  isConnected: boolean;
}

interface CollaborationContextType {
  state: CollaborationState;
  joinRoom: (roomId: string, user: Omit<UserPresence, 'cursor' | 'lastActivity'>) => void;
  leaveRoom: () => void;
  updateCursor: (cursor: UserPresence['cursor']) => void;
  setTyping: (isTyping: boolean) => void;
  broadcastChange: (change: any) => void;
  resolveConflict: (change1: any, change2: any) => any;
}

const CollaborationContext = createContext<CollaborationContextType | null>(null);

// Generate random user colors
const userColors = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
];

const getRandomColor = () => userColors[Math.floor(Math.random() * userColors.length)];

export const CollaborationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { connection, connectionState } = useSignalRStore();
  const [state, setState] = useState<CollaborationState>({
    roomId: null,
    users: [],
    currentUser: null,
    isConnected: false,
  });

  // Handle SignalR connection
  useEffect(() => {
    if (connection && connectionState === HubConnectionState.Connected) {
      setState(prev => ({ ...prev, isConnected: true }));

      // Set up SignalR event handlers
      connection.on('UserJoined', (user: UserPresence) => {
        setState(prev => ({
          ...prev,
          users: [...prev.users.filter(u => u.id !== user.id), user],
        }));
      });

      connection.on('UserLeft', (userId: string) => {
        setState(prev => ({
          ...prev,
          users: prev.users.filter(u => u.id !== userId),
        }));
      });

      connection.on('CursorUpdated', (userId: string, cursor: UserPresence['cursor']) => {
        setState(prev => ({
          ...prev,
          users: prev.users.map(u =>
            u.id === userId ? { ...u, cursor, lastActivity: Date.now() } : u
          ),
        }));
      });

      connection.on('UserTyping', (userId: string, isTyping: boolean) => {
        setState(prev => ({
          ...prev,
          users: prev.users.map(u =>
            u.id === userId ? { ...u, isTyping, lastActivity: Date.now() } : u
          ),
        }));
      });

      connection.on('ContentChanged', (change: any) => {
        // Handle collaborative content changes
        handleContentChange(change);
      });
    } else {
      setState(prev => ({ ...prev, isConnected: false }));
    }

    return () => {
      if (connection) {
        connection.off('UserJoined');
        connection.off('UserLeft');
        connection.off('CursorUpdated');
        connection.off('UserTyping');
        connection.off('ContentChanged');
      }
    };
  }, [connection, connectionState]);

  const handleContentChange = useCallback((change: any) => {
    // Handle incoming collaborative changes
    // This would integrate with the editor's change handling
    console.log('Received collaborative change:', change);
  }, []);

  const joinRoom = useCallback(async (roomId: string, user: Omit<UserPresence, 'cursor' | 'lastActivity'>) => {
    if (!connection || connectionState !== HubConnectionState.Connected) return;

    const userPresence: UserPresence = {
      ...user,
      color: user.color || getRandomColor(),
      cursor: { line: 0, column: 0, visible: false },
      lastActivity: Date.now(),
    };

    try {
      await connection.invoke('JoinRoom', roomId, userPresence);
      setState(prev => ({
        ...prev,
        roomId,
        currentUser: userPresence,
      }));
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  }, [connection, connectionState]);

  const leaveRoom = useCallback(async () => {
    if (!connection || !state.roomId) return;

    try {
      await connection.invoke('LeaveRoom', state.roomId);
      setState(prev => ({
        ...prev,
        roomId: null,
        users: [],
        currentUser: null,
      }));
    } catch (error) {
      console.error('Failed to leave room:', error);
    }
  }, [connection, state.roomId]);

  const updateCursor = useCallback(async (cursor: UserPresence['cursor']) => {
    if (!connection || !state.roomId || !state.currentUser) return;

    try {
      await connection.invoke('UpdateCursor', state.roomId, cursor);
      setState(prev => ({
        ...prev,
        currentUser: prev.currentUser ? { ...prev.currentUser, cursor } : null,
      }));
    } catch (error) {
      console.error('Failed to update cursor:', error);
    }
  }, [connection, state.roomId, state.currentUser]);

  const setTyping = useCallback(async (isTyping: boolean) => {
    if (!connection || !state.roomId) return;

    try {
      await connection.invoke('SetTyping', state.roomId, isTyping);
    } catch (error) {
      console.error('Failed to set typing status:', error);
    }
  }, [connection, state.roomId]);

  const broadcastChange = useCallback(async (change: any) => {
    if (!connection || !state.roomId) return;

    try {
      await connection.invoke('BroadcastChange', state.roomId, change);
    } catch (error) {
      console.error('Failed to broadcast change:', error);
    }
  }, [connection, state.roomId]);

  const resolveConflict = useCallback((change1: any, change2: any): any => {
    // Simple conflict resolution: prefer the change from the user with higher priority
    // In a real implementation, this would be more sophisticated
    const priority1 = change1.userPriority || 1;
    const priority2 = change2.userPriority || 1;

    return priority1 >= priority2 ? change1 : change2;
  }, []);

  // Clean up inactive users
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setState(prev => ({
        ...prev,
        users: prev.users.filter(u => now - u.lastActivity < 300000), // 5 minutes
      }));
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const contextValue: CollaborationContextType = {
    state,
    joinRoom,
    leaveRoom,
    updateCursor,
    setTyping,
    broadcastChange,
    resolveConflict,
  };

  return (
    <CollaborationContext.Provider value={contextValue}>
      {children}
    </CollaborationContext.Provider>
  );
};

export const useCollaboration = () => {
  const context = useContext(CollaborationContext);
  if (!context) {
    throw new Error('useCollaboration must be used within a CollaborationProvider');
  }
  return context;
};